import React, { useState } from "react";
import {
	CCButton,
	CCField,
	CCModal,
	CCSpinner,
	useToast,
} from "@pymblecitizencoders/cc-ui";
import { Info } from "lucide-react";
import {
	publishFeedback,
	publishStatusLabel,
	refusalDetail,
	usePublishWorkflow,
} from "../../hooks/useEventPublish";
import { PublishChain } from "./PublishChain";
import { Tooltip } from "../common/Tooltip";
import { formatWhen } from "../../utils/eventWhen";
import { knownName } from "../../utils/personName";
import "./eventManager.css";

interface EventSignOffPanelProps {
	eventId: string;
	readOnly: boolean;
	/** The event's type, which is what its chain is chosen by. Shown before
	    submission so the chain is not discovered only after sending it. */
	eventType?: string | null;
	/** Whether this person may pull a released event back — `event_admin` or
	    `event_publisher`, decided by the caller since only it has the
	    permissions payload. The API enforces it regardless. */
	canWithdraw?: boolean;
	/**
	 * Unsaved edits sitting in the editor's draft.
	 *
	 * Submitting sends nothing but the event id: the API snapshots the chain and
	 * reads the STORED document. So an unsaved edit is not part of what goes to
	 * the approvers, and the organiser has no way to tell — the panel would say
	 * "In review" over a version nobody on screen is looking at. Blocked here
	 * rather than warned about after the fact.
	 */
	dirty?: boolean;
}

/** The API refuses a withdrawal with a shorter reason, as it does a decline. */
const MIN_REASON = 10;

/** The pill wording, matching the chain rows and the sign-off list. */
/**
 * Where this event sits in the publishing chain, in the editor's right-hand
 * sidebar.
 *
 * This WAS a tab. A tab meant the one thing an organiser opens the editor to
 * check — whether the event is released yet, and who it is waiting on — was
 * behind a click, and behind a click on a tab bar whose other seven entries are
 * all edit forms. It sits beside the release toggle now because the two answer
 * the same question from different ends: the toggle is carer permission for a
 * child to attend, this is colleagues releasing the event so carers can see it
 * at all. Having them apart is what made them easy to confuse.
 *
 * Read-only apart from Submit. Decisions are taken on the sign-off screen, which
 * is where an approver works through everything waiting on them; making them
 * possible here as well would mean two places to keep honest for no gain.
 *
 * A `<details>` accordion, because a six-step chain is taller than everything
 * else in the sidebar and it sits directly above Save. Native rather than a
 * hand-rolled toggle: cc-ui has no accordion, and `<details>`/`<summary>` brings
 * the disclosure semantics, the keyboard handling and the focus ring with it.
 *
 * ONLY THE STATES THAT DRAW A CHAIN COLLAPSE. Loading, error and never-submitted
 * are each a line or two — an accordion over them would hide the Submit button
 * behind a click for no saving. It also means the accordion never appears until
 * the status is known, so it cannot render open and then snap shut when the
 * fetch lands.
 */
export const EventSignOffPanel: React.FC<EventSignOffPanelProps> = ({
	eventId,
	readOnly,
	eventType,
	canWithdraw = false,
	dirty = false,
}) => {
	const toast = useToast();
	const {
		workflow,
		workflowLoading,
		workflowError,
		workflowFetching,
		submit,
		submitting,
		withdraw,
		withdrawing,
	} = usePublishWorkflow(eventId);
	/** The reason box, open only while withdrawing. */
	const [reason, setReason] = useState("");
	const [pulling, setPulling] = useState(false);
	const [reasonError, setReasonError] = useState("");
	/* Submitting, or checking what the submit did. Both mean the answer on
	   screen is not settled yet, and neither should accept another click. */
	const busy = submitting || workflowFetching;
	/* Both submit buttons, first and re-submit alike: a corrected event going
	   back round is exactly when unsaved edits are most likely and most costly,
	   since the correction is the whole point of the resubmission. */
	const blocked = busy || dirty;
	/* Rendered above each button rather than only as a disabled tooltip: a
	   disabled control that does not say why reads as broken, and the reason is
	   not guessable from the button. */
	const unsavedNote = dirty ? (
		<span className="em-signoff__unsaved" role="status">
			Unsaved changes. Save them before submitting: approvers read the last
			saved version, not what is on screen.
		</span>
	) : null;
	const submitTitle = dirty
		? "Save your changes before submitting"
		: undefined;
	/* The modal. The panel is a status pill in the page header; the chain and
	   its actions open from it. */
	const [modalOpen, setModalOpen] = useState(false);

	const onWithdraw = async () => {
		/* The API refuses anything shorter with a 422; saying so here means the
		   person finds out before the round trip. */
		if (reason.trim().length < MIN_REASON) {
			setReasonError(`Give a reason of at least ${MIN_REASON} characters.`);
			return;
		}
		try {
			await withdraw(reason.trim());
			setPulling(false);
			setReason("");
			toast.show({
				message: "Withdrawn. Carers can no longer see this event.",
				severity: "success",
				duration: 4000,
			});
		} catch (error) {
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not withdraw this event. Please try again.",
				severity: "danger",
				duration: 5000,
			});
		}
	};

	const onSubmit = async () => {
		try {
			await submit();
			toast.show({
				message: "Submitted for sign-off.",
				severity: "success",
				duration: 3000,
			});
		} catch (error) {
			/* Say which. The API names the reason on a refusal — already in
			   review, or the roles nobody holds — and guessing at it in the
			   client is what made this message useless. */
			toast.show({
				message:
					refusalDetail(error) ??
					"Could not submit this event. Please try again.",
				severity: "danger",
				duration: 6000,
			});
		}
	};

	const status = workflow?.Status ?? null;

	const currentStepName =
		workflow && typeof workflow.CurrentStep === "number"
			? (workflow.Steps[workflow.CurrentStep]?.Name ??
				workflow.Steps[workflow.CurrentStep]?.RoleId)
			: null;

	const body = (): React.ReactNode => {
		if (workflowLoading) {
			return (
				<div className="em-field em-signoff">
					<CCSpinner size="sm" label="Loading sign-off..." />
				</div>
			);
		}

		/* Not knowing is not the same as not submitted. Without this the panel
		   answered an unreachable API by declaring the event unsubmitted and
		   offering the button, which is the one thing it must not do when the event
		   may well be halfway through its chain. */
		if (workflowError) {
			return (
				<div className="em-field em-signoff">
					<span className="em-muted">
						The sign-off state could not be loaded, so it is not shown here.
						Reload the page to try again.
					</span>
				</div>
			);
		}

		/* `GET {id}/publish/` ALWAYS answers with an object: for an event that was
		   never submitted it returns one whose `Status` is null. Testing the object
		   itself finds a workflow that is not there, and the status label below would
		   then read "In review" over an empty chain. */
		if (!workflow?.Status) {
			return (
				<div className="em-field em-signoff">
					<span className="em-muted">
						Not submitted. Carers and attendees cannot see this event until it has
						been through its approvers.
					</span>
					{/* ONLY WHEN THERE IS NO TYPE. Naming the type back to someone who
					    set it, on a panel a click away from the field they set it in,
					    spent a line saying what routing an event is FOR. The absence is
					    the part worth reporting: it is not an error, it just means the
					    event resolves on the calendar it came from instead, and nothing
					    else on screen would tell you that.

					    Both halves of this used to be wrong. The chain was chosen by the
					    calendar until the API began routing on the type, and a typeless
					    event still falls to its CALENDAR before the default. */}
					{!eventType && (
						<span className="em-muted">
							It has no event type, so it follows the workflow for the calendar
							it came from, or the default. Set one on the Details tab to route
							it.
						</span>
					)}
					{!readOnly && unsavedNote}
					{!readOnly && (
						<CCButton
							variant="solid"
							colour="primary"
							fullWidth
							className="em-mt-sm"
							disabled={blocked}
							title={submitTitle}
							onClick={onSubmit}
						>
							{busy ? "Submitting..." : "Submit for sign-off"}
						</CCButton>
					)}
				</div>
			);
		}


		if (!workflow || !status) return null;

	/* Why it stopped, and who stopped it. From History, because a re-submit
	   wipes `Steps` and an invalidation never writes one. */
	const feedback = publishFeedback(workflow);
	/* One sentence, used as the tooltip's label, its accessible name and the
	   native title — three places that must not drift apart. */
	/* Whether the chain itself is already showing the note. */
	const stepHasReason = workflow.Steps.some((step) => !!step.Reason);
	const feedbackTitle = (() => {
		if (!feedback) return "";
		const who = knownName(feedback.by);
		const when = feedback.at ? `, ${formatWhen(feedback.at)}` : "";
		if (feedback.kind === "invalidated") {
			return `${who ? `Edited by ${who}` : "Edited after sign-off"}${when}`;
		}
		const verb =
			feedback.kind === "declined" ? "Declined" : "Changes requested";
		return `${who ? `${verb} by ${who}` : verb}${when}`;
	})();
	return (
		<div className="em-field em-signoff">
			{/* Say where a STOPPED workflow stands, and what to do about it — a
			    pill and a list of steps do not carry that on their own.
			    
			    Nothing for `in_review`: the summary row above already names the
			    step it is with, the chain below shows the same step waiting, and
			    the pill says In review. A third telling was noise. */}
			{status !== "in_review" && (
				<span className="em-muted em-signoff__state">
					{status === "published" ? (
						<>
							Approved by everyone in the workflow. Carers and attendees can see
							it.
						</>
					) : status === "declined" ? (
						<>Declined. Make the changes it needs and submit it again.</>
					) : feedback?.kind === "invalidated" ? (
						/* Nobody asked for changes here — the event was edited after it
						   had been signed off, which drops the workflow by design.
						   Saying "sent back for changes" sent people looking for a note
						   that was never written. */
						<>
							Edited after sign-off, so the approvals no longer stand. Submit
							again to run the workflow from the start.
						</>
					) : (
						<>Sent back for changes. Submit again to restart the workflow.</>
					)}
				</span>
			)}

			{/* The note, on the pill rather than under it.
			    
			    A card in a 360px sidebar column has no room to spare, and the
			    reason is not something anyone reads twice — it is read once,
			    acted on, and then it is in the way. The trigger stays visible and
			    says there is something to read; `interactive` because it is a
			    real button, so it is reachable by keyboard rather than being a
			    hover-only fact. */}
			{/* Only when no step carries it. A decision that is still in `Steps`
			    is shown on its own row, where it belongs — this chip is for the
			    reasons that live nowhere else: an invalidation, or a note whose
			    step was replaced by a re-submit. */}
			{feedback && !stepHasReason && (
				<Tooltip
					label={feedbackTitle}
					details={
						feedback.reason ? (
							<span className="em-tip__desc">{feedback.reason}</span>
						) : undefined
					}
					align="start"
					interactive
					className="em-signoff__why-wrap"
				>
					<button
						type="button"
						className={`em-signoff__why is-${feedback.kind}`}
						/* Native title as well: the tooltip is a hover affordance,
						   and a touch device has no hover. */
						title={
							feedback.reason
								? `${feedbackTitle} — ${feedback.reason}`
								: feedbackTitle
						}
					>
						<Info size={13} aria-hidden="true" />
						{feedback.kind === "invalidated" ? "What changed" : "Why"}
					</button>
				</Tooltip>
			)}

			<div className="em-signoff__chain">
				<PublishChain
					steps={workflow.Steps}
					currentStep={workflow.CurrentStep}
					inReview={status === "in_review"}
					compact
				/>
			</div>

			{/* Taking a released event back. Inline rather than in a dialog: the
			    panel is already a disclosure, and the reason is the whole of the
			    interaction. Two steps, because this reverses what every approver
			    in the chain agreed to and carers may have responded to it. */}
			{!readOnly && canWithdraw && status === "published" && !pulling && (
				<CCButton
					variant="outline"
					colour="danger"
					fullWidth
					onClick={() => {
						setReason("");
						setReasonError("");
						setPulling(true);
					}}
				>
					Withdraw from the calendar
				</CCButton>
			)}

			{pulling && (
				<div className="em-signoff__withdraw">
					<span className="em-muted">
						Carers stop seeing this event, and it goes back to you. Submitting
						again restarts the workflow from the first approver.
					</span>
					<CCField
						id="em-signoff-withdraw-reason"
						label="Reason"
						multiline
						rows={3}
						fullWidth
						autoFocus
						error={reasonError || undefined}
						value={reason}
						onChange={(e) => {
							setReason(e.target.value);
							if (reasonError) setReasonError("");
						}}
					/>
					<div className="em-signoff__withdraw-actions">
						<CCButton
							variant="outline"
							size="sm"
							onClick={() => setPulling(false)}
						>
							Cancel
						</CCButton>
						<CCButton
							variant="solid"
							colour="danger"
							size="sm"
							disabled={withdrawing}
							onClick={onWithdraw}
						>
							{withdrawing ? "Withdrawing..." : "Withdraw"}
						</CCButton>
					</div>
				</div>
			)}

			{/* Declined and changes-requested both accept a fresh submit, which is
			    how an organiser gets a corrected event moving again. */}
			{!readOnly &&
				(status === "declined" || status === "changes_requested") && (
					<>
						{unsavedNote}
						<CCButton
							variant="solid"
							colour="primary"
							fullWidth
							disabled={blocked}
							title={submitTitle}
							onClick={onSubmit}
						>
							{busy ? "Submitting..." : "Submit again"}
						</CCButton>
					</>
				)}
		</div>
		);
	};

	/* Always the status, never the chain: the pill answers "where is this up
	   to" without opening anything, and the modal holds the detail. */
	const pillLabel = workflowLoading
		? "Loading"
		: workflowError
			? "Unavailable"
			: status
				? publishStatusLabel(status)
				: "Not submitted";

	return (
		<>
			<button
				type="button"
				className="em-signoff-trigger"
				onClick={() => setModalOpen(true)}
				title={
					status === "in_review" && currentStepName
						? `With ${currentStepName}`
						: undefined
				}
			>
				<span className="em-signoff-trigger__label">Sign-off</span>
				<span className={`em-approve-status is-${status ?? "none"}`}>
					{pillLabel}
				</span>
			</button>
			<CCModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				title="Sign-off"
			>
				{body()}
			</CCModal>
		</>
	);
};
